<script setup lang="ts">
import { computed } from 'vue'
import DatePicker from 'primevue/datepicker'

/*
 * Wrapper around PrimeVue DatePicker that:
 *   - uses ISO-ish "YYYY-MM-DD HH:mm:ss" string for v-model (backend format)
 *   - shows a localised popup calendar with a time picker
 *   - allows direct typing in the input (with parsing on blur via PrimeVue)
 *
 * The component is intentionally string-in / string-out so it composes cleanly
 * with VeeValidate / Zod schemas that work with backend datetime strings.
 *
 * Locale strings (day/month names, button labels, today/clear) come from the
 * global PrimeVue locale config (cs/en switched via vue-i18n in main.ts).
 */
const props = withDefaults(
    defineProps<{
        modelValue?: string | null
        invalid?: boolean
        showTime?: boolean
        showSeconds?: boolean
        placeholder?: string
        disabled?: boolean
    }>(),
    {
        modelValue: null,
        showTime: true,
        showSeconds: false
    }
)

const emit = defineEmits<{
    (e: 'update:modelValue', v: string | null): void
}>()

function pad(n: number): string {
    return n.toString().padStart(2, '0')
}

function dateToString(d: Date | null): string | null {
    if (!d) return null
    const y = d.getFullYear()
    const m = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    if (!props.showTime) return `${y}-${m}-${day}`
    const h = pad(d.getHours())
    const mi = pad(d.getMinutes())
    if (!props.showSeconds) return `${y}-${m}-${day} ${h}:${mi}`
    const s = pad(d.getSeconds())
    return `${y}-${m}-${day} ${h}:${mi}:${s}`
}

function stringToDate(s: string | null | undefined): Date | null {
    if (!s) return null
    // Accept "YYYY-MM-DD", "YYYY-MM-DD HH:mm[:ss]", "YYYY-MM-DDTHH:mm[:ss][.ms][Z|+hh:mm]"
    const normalised = s.replace(' ', 'T')
    const d = new Date(normalised)
    return isNaN(d.getTime()) ? null : d
}

const dateValue = computed<Date | null>({
    get: () => stringToDate(props.modelValue),
    set: v => emit('update:modelValue', dateToString(v))
})
</script>

<template>
    <DatePicker
        v-model="dateValue"
        :show-time="showTime"
        :show-seconds="showSeconds"
        :show-icon="true"
        icon-display="input"
        hour-format="24"
        :show-button-bar="true"
        :placeholder="placeholder ?? (showTime ? 'DD. MM. YYYY HH:mm' : 'DD. MM. YYYY')"
        :invalid="invalid"
        :disabled="disabled"
        date-format="dd. mm. yy"
        :first-day-of-week="1"
        :manual-input="true"
        class="w-full"
    />
</template>
